<template>
  <div class="indications-container">
    <page-loading
      v-if="loading"
      message="Carregando indicações"
      subtitle="Calculando resumo e histórico"
    />
    <template v-else>
    <page-toolbar class="mb-4">
      <template #filters>
        <v-dialog ref="dialog" v-model="dialog" persistent width="290px">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="formattedPeriod"
              label="Período"
              prepend-inner-icon="mdi-calendar"
              readonly
              dense
              outlined
              hide-details
              class="br-form-field"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker v-model="selectedRange" range locale="pt-br">
            <v-btn @click="closeDialog" color="primary" text>Cancelar</v-btn>
            <v-btn @click="handleLoadIndications" color="primary">Aplicar</v-btn>
          </v-date-picker>
        </v-dialog>
      </template>
    </page-toolbar>

    <section class="page-summary mb-4">
      <h2 class="br-section-title">Resumo</h2>
      <v-row dense>
        <v-col cols="12" sm="6" md="3">
          <partner-summary-metric-card
            title="Total de indicações"
            :value="totalIndications"
            icon="mdi-account-plus-outline"
            variant="brand"
            tooltip="Quantidade de indicações no período selecionado."
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <partner-summary-metric-card
            title="Total em bônus"
            :value="formatPrice(totalBonus)"
            icon="mdi-wallet-outline"
            variant="finance"
            tooltip="Soma dos valores de bônus das indicações no período."
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <partner-summary-metric-card
            title="Média por indicação"
            :value="formatPrice(averageBonus)"
            icon="mdi-chart-timeline-variant"
            variant="network"
            tooltip="Média do valor de bônus por indicação no período."
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <partner-summary-metric-card
            title="Período"
            :value="periodText"
            icon="mdi-calendar-range"
            variant="slate"
            multiline
            tooltip="Intervalo de datas aplicado à lista e aos totais."
          />
        </v-col>
      </v-row>
    </section>

    <data-table-card
      title="Lista de indicações"
      icon="mdi-account-plus-outline"
      :headers="indicationsHeaders"
      :items="formattedIndications"
      :loading="indicationsLoading"
      :items-per-page="15"
      searchable
      :search.sync="search"
      search-label="Pesquise por cliente ou valor"
      show-count
      loading-text="Carregando indicações..."
      no-data-text="Nenhuma indicação encontrada no período selecionado"
    />
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { GetAllIndications } from "@/services/indications/GetAllIndications";
import { SaleType } from "@/types/sale/Sale";
import PartnerSummaryMetricCard from "@/components/summary/PartnerSummaryMetricCard.vue";
import PageLoading from "@/components/loading/PageLoading.vue";
import PageToolbar from "@/components/ui/PageToolbar.vue";
import DataTableCard from "@/components/ui/DataTableCard.vue";

export default defineComponent({
  components: { DataTableCard, PageLoading, PageToolbar, PartnerSummaryMetricCard },
  data() {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    return {
      loading: true,
      indicationsLoading: false,
      dialog: false,
      search: "",
      selectedRange: [
        thirtyDaysAgo.toISOString().split('T')[0],
        today.toISOString().split('T')[0]
      ],
      startDate: thirtyDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
      indications: [] as SaleType[],
    };
  },
  computed: {
    formattedPeriod(): string {
      const parts = (this.selectedRange || [])
        .filter(Boolean)
        .map((date: string) => {
          const [year, month, day] = date.split("-");
          return `${day}/${month}/${year}`;
        });
      return parts.join(" — ") || "Selecione o período";
    },
    indicationsHeaders() {
      return [
        { text: "Data", value: "createdAt", sortable: true },
        { text: "Cliente", value: "customerName", sortable: true },
        { text: "Valor", value: "value", sortable: true },
      ];
    },
    formattedIndications() {
      return (this as any).indications.map((item: SaleType) => ({
        ...item,
        createdAt: this.formatDate(item.createdAt),
        value: this.formatPrice(item.value)
      }));
    },
    totalIndications(): number {
      return this.indications.length;
    },
    totalBonus(): number {
      return this.indications.reduce((total, item) => total + (item.value || 0), 0);
    },
    averageBonus(): number {
      if (this.indications.length === 0) return 0;
      return this.totalBonus / this.indications.length;
    },
    periodText(): string {
      if (this.selectedRange && this.selectedRange.length === 2) {
        const start = this.selectedRange[0];
        const end = this.selectedRange[1];
        if (start && end) {
          const startDate = new Date(start);
          const endDate = new Date(end);
          return `${startDate.toLocaleDateString('pt-BR')} - ${endDate.toLocaleDateString('pt-BR')}`;
        }
      }
      return "Últimos 30 dias";
    }
  },
  methods: {
    closeDialog() {
      this.dialog = false;
    },
    async handleLoadIndications() {
      this.loading = true;
      if (this.selectedRange.length > 0) {
        if (this.selectedRange[0] > this.selectedRange[1]) {
          this.startDate = this.selectedRange[1];
          this.endDate = this.selectedRange[0];
        } else {
          this.startDate = this.selectedRange[0];
          this.endDate = this.selectedRange[1];
        }
      }
      await this.loadIndications();
      this.loading = false;
      this.closeDialog();
    },
    async loadIndications() {
      this.indicationsLoading = true;
      try {
        const getAllIndications = new GetAllIndications();
        const indicationsData = await getAllIndications.execute(this.startDate, this.endDate);
        this.indications = indicationsData;
      } catch (error) {
        console.error("Erro ao carregar indicações:", error);
        this.indications = [];
        this.$store.dispatch("showSnack", {
          message: "Erro ao carregar indicações",
          color: "error",
        });
      } finally {
        this.indicationsLoading = false;
      }
    },
    formatDate(dateString: string): string {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR");
    },
    formatPrice(value: any): string {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (!numValue && numValue !== 0) return "R$ 0,00";
      const formattedValue = numValue.toFixed(2).replace(".", ",");
      return `R$ ${formattedValue}`;
    },
  },
  async mounted() {
    await this.loadIndications();
    this.loading = false;
  },
});
</script>

<style scoped lang="scss">
.indications-container {
  padding: 0;
}
</style>
